"use client";

import React, { useMemo } from "react";

import { Card } from "@/components/flowbite/card";
import { TruncatedTextWithTooltip } from "@/components/flowbite/truncated-text-with-tooltip";
import Typography from "@/components/flowbite/typography/typography";
import { ArrowRightIcon } from "@/components/icons-lib";
import { PolygonPreview } from "@/components/maps/polygon-preview";
import { getBoundaryPolygonPreviewColors } from "@/providers/maps/utils/get-boundary-polygon-preview-colors";

const PREVIEW_SIZE = 40;

export interface BoundaryZoneListItemCardProps {
  label: string;
  previewCoordinates?: number[][];
  schoolId?: string | null;
  previewColor?: string;
  onClick: () => void;
}

const BoundaryZoneListItemCardComponent: React.FC<BoundaryZoneListItemCardProps> = ({
  label,
  previewCoordinates,
  schoolId,
  previewColor,
  onClick,
}) => {
  const polygonTint = useMemo(
    () =>
      getBoundaryPolygonPreviewColors({
        schoolId,
        color: previewColor,
      }),
    [previewColor, schoolId],
  );

  const hasPreview = previewCoordinates != null && previewCoordinates.length > 0;

  return (
    <Card
      aria-label={`Open ${label}`}
      as="button"
      className="flex w-full min-w-0 items-center justify-start gap-1 py-1 pl-3 pr-2 text-left"
      type="button"
      onClick={onClick}
    >
      {hasPreview ? (
        <PolygonPreview
          className="shrink-0"
          coordinates={previewCoordinates}
          size={PREVIEW_SIZE}
          {...polygonTint}
        />
      ) : (
        <span className="h-10 w-10 shrink-0 rounded bg-gray-100" />
      )}

      <Typography.BodyMRegular
        capitalize={false}
        className="min-w-0 flex-1 text-[#161515]"
        colorShade={500}
      >
        <TruncatedTextWithTooltip text={label} />
      </Typography.BodyMRegular>

      <span
        aria-hidden
        className="flex h-8 w-8 shrink-0 items-center justify-center text-[#161515]"
      >
        <ArrowRightIcon className="h-5 w-5 shrink-0 stroke-[#161515] text-[#161515]" />
      </span>
    </Card>
  );
};

export const BoundaryZoneListItemCard = React.memo(BoundaryZoneListItemCardComponent);
